import { KEM_SOCKET_HOST } from "@ch/api";
import { StorageNamesEnum } from "@ch/helpers/utils/enums";
import { filterNullishFromObject } from "@ch/helpers/utils";
import localforage from "localforage";
import { parseKemCitationResources } from "./kemCitations";
import type { KemCitationResource } from "./types";

export type KemQaSocketState =
  | "DISCONNECTED"
  | "CONNECTING"
  | "CONNECTED"
  | "READY"
  | "STREAMING"
  | "COMPLETE"
  | "ERROR"
  | "RECONNECTING";

export type KemQaMemberPayload = Record<string, unknown>;

export interface KemQaCompletionData {
  output_audio_url?: string;
  kem_message_id?: string;
  user_message_id?: string;
  infographic?: string;
  infographic_messageid?: string;
  remaining_tokens?: number;
  resources?: KemCitationResource[];
}

export interface KemQaChatAudioPayload {
  text: string;
  audio_url: string;
}

export interface KemQaSocketCallbacks {
  onStateChange?: (state: KemQaSocketState) => void;
  onChunk?: (chunk: string) => void;
  onComplete?: (data: KemQaCompletionData) => void;
  onError?: (error: string) => void;
  onMemberAck?: () => void;
  /** Fired for each `thinking:` frame received before chat output. */
  onThinking?: (payload: string) => void;
}

class KemQaSocket {
  private ws: WebSocket | null = null;
  private callbacks: KemQaSocketCallbacks = {};
  private state: KemQaSocketState = "DISCONNECTED";
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private isIntentionallyClosed = false;
  private expectingMetadata = false;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private static getWebSocketUrl(): string {
    const socketHost = String(KEM_SOCKET_HOST);
    const baseUrl = socketHost.replace(/^https?:\/\//, "");
    const protocol = socketHost.startsWith("https") ? "wss" : "ws";
    return `${protocol}://${baseUrl}/ws/qachat`;
  }

  private setState(newState: KemQaSocketState) {
    this.state = newState;
    this.callbacks.onStateChange?.(newState);
  }

  private calculateReconnectDelay(): number {
    return Math.min(
      this.baseReconnectDelay * 2 ** (this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );
  }

  private static async getRefreshToken(): Promise<string | null> {
    try {
      const token = await localforage.getItem<string>(
        StorageNamesEnum.REFRESH_TOKEN,
      );
      return token ?? null;
    } catch (error) {
      // console.log("[KemQaSocket] Failed to get refresh token:", error);
      return null;
    }
  }

  async connect(callbacks: KemQaSocketCallbacks): Promise<void> {
    if (this.state === "CONNECTING" || this.state === "CONNECTED") {
      // console.log("[KemQaSocket] Already connecting/connected, skipping");
      return;
    }

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.callbacks = callbacks;
    this.isIntentionallyClosed = false;

    try {
      const refreshToken = await KemQaSocket.getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token found");
      }

      this.setState("CONNECTING");

      const url = `${KemQaSocket.getWebSocketUrl()}?x_token=${refreshToken}`;
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        // console.log("[KemQaSocket] WebSocket connected");
        this.setState("CONNECTED");
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(String(event.data));
      };

      this.ws.onerror = (error: Event) => {
        console.log("[KemQaSocket] WebSocket error", error);
      };

      this.ws.onclose = (event: CloseEvent) => {
        // console.log("[KemQaSocket] WebSocket closed", event.code, event.reason);

        if (this.isIntentionallyClosed) {
          // console.log("[KemQaSocket] Intentionally closed, not reconnecting");
          this.setState("DISCONNECTED");
          return;
        }

        if (event.code === 1008 || event.reason === "unauthorized") {
          // console.log("[KemQaSocket] Auth error, not reconnecting");
          this.setState("ERROR");
          this.callbacks.onError?.("unauthorized");
          return;
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts += 1;
          const delay = this.calculateReconnectDelay();

          // console.log(
          //   `[KemQaSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
          // );

          this.setState("RECONNECTING");

          if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
          }

          this.reconnectTimeoutId = setTimeout(() => {
            // console.log("[KemQaSocket] Attempting reconnection...");
            void this.connect(this.callbacks);
          }, delay);
        } else {
          // console.log(
          //   "[KemQaSocket] Max reconnection attempts reached, giving up",
          // );
          this.setState("ERROR");
          this.callbacks.onError?.(
            "Connection lost. Please check your internet connection.",
          );
        }
      };
    } catch (error) {
      this.setState("ERROR");
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      this.callbacks.onError?.(errorMessage);
    }
  }

  private handleMessage(data: string) {
    if (data === "member-payload:ok") {
      this.setState("READY");
      this.callbacks.onMemberAck?.();
      return;
    }

    if (data === "error:400-unauthorized") {
      this.setState("ERROR");
      this.callbacks.onError?.("unauthorized");
      return;
    }

    if (data.startsWith("thinking:")) {
      const payload = data.substring("thinking:".length);
      this.callbacks.onThinking?.(payload);
      return;
    }

    if (this.expectingMetadata && data.startsWith("{")) {
      this.expectingMetadata = false;
      this.setState("COMPLETE");
      this.parseCompletionData(data);
      return;
    }

    if (data.startsWith("chat-output:")) {
      const content = data.substring("chat-output:".length);

      if (content.trim() === "response_complete") {
        this.expectingMetadata = true;
        return;
      }

      this.callbacks.onChunk?.(content);

      if (this.state !== "STREAMING") {
        this.setState("STREAMING");
      }
    }
  }

  private parseCompletionData(data: string) {
    try {
      const parsedData = JSON.parse(data) as Record<string, unknown>;

      const remainingTokens =
        typeof parsedData.remaining_tokens === "number"
          ? parsedData.remaining_tokens
          : typeof parsedData.remaining_tokens === "string"
            ? Number(parsedData.remaining_tokens)
            : undefined;

      const completionData: KemQaCompletionData = {
        output_audio_url:
          typeof parsedData.output_audio_url === "string"
            ? parsedData.output_audio_url
            : undefined,
        kem_message_id:
          typeof parsedData.kem_message_id === "string"
            ? parsedData.kem_message_id
            : undefined,
        user_message_id:
          typeof parsedData.user_message_id === "string"
            ? parsedData.user_message_id
            : undefined,
        infographic:
          typeof parsedData.infographic === "string"
            ? parsedData.infographic
            : undefined,
        infographic_messageid:
          typeof parsedData.infographic_messageid === "string"
            ? parsedData.infographic_messageid
            : undefined,
        remaining_tokens: Number.isFinite(remainingTokens)
          ? remainingTokens
          : undefined,
        resources: parseKemCitationResources(parsedData.resources),
      };

      this.callbacks.onComplete?.(completionData);
    } catch (error) {
      // console.error("[KemQaSocket] Error parsing completion data:", error);
      this.callbacks.onComplete?.({});
    }
  }

  sendMemberPayload(payload: KemQaMemberPayload) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError?.("WebSocket is not connected");
      return false;
    }

    const cleanedPayload = filterNullishFromObject(payload);

    // console.log("[KemQaSocket] Sending member payload:", cleanedPayload);

    const message = `member-payload:${JSON.stringify(cleanedPayload)}`;
    this.ws.send(message);
    return true;
  }

  sendChatMessage(message: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError?.("WebSocket is not connected");
      return false;
    }

    if (this.state === "STREAMING") {
      this.callbacks.onError?.("Cannot send message while streaming");
      return false;
    }

    this.expectingMetadata = false;

    const chatMessage = `chat-incoming:${message}`;
    this.ws.send(chatMessage);
    return true;
  }

  sendChatAudio(payload: KemQaChatAudioPayload) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError?.("WebSocket is not connected");
      return false;
    }

    if (this.state === "STREAMING") {
      this.callbacks.onError?.("Cannot send message while streaming");
      return false;
    }

    this.expectingMetadata = false;

    const chatAudioMessage = `chat-audio:${JSON.stringify({
      text: payload.text,
      audio_url: payload.audio_url,
    })}`;

    this.ws.send(chatAudioMessage);
    return true;
  }

  disconnect() {
    // console.log("[KemQaSocket] Disconnecting...");
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = 0;
    this.expectingMetadata = false;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setState("DISCONNECTED");
  }

  reconnect() {
    // console.log("[KemQaSocket] Manual reconnection requested");
    this.reconnectAttempts = 0;
    this.isIntentionallyClosed = false;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    void this.connect(this.callbacks);
  }

  getState(): KemQaSocketState {
    return this.state;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export default KemQaSocket;
